import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";

function getRoles(userInfo) {
  const value = userInfo?.roles ?? userInfo?.role;

  if (Array.isArray(value)) {
    return value
      .map((role) => String(role).trim().toLowerCase())
      .filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean);
}

function hasRole(userInfo, targetRole) {
  return getRoles(userInfo).includes(
    String(targetRole || "")
      .trim()
      .toLowerCase(),
  );
}

export default function AdminRoute({ children }) {
  const location = useLocation();
  const {
    checked = false,
    loading = false,
    userInfo = null,
    isAuthenticated = false,
  } = useSelector((state) => state.userAuth || {});

  const userId = userInfo?.id || userInfo?._id;
  const isStoreManager = hasRole(userInfo, "store_manager");
  const isSuperAdmin = hasRole(userInfo, "super_admin");
  const isLegacyAdmin = hasRole(userInfo, "admin");
  const hasAdminPortalAccess = isLegacyAdmin || isSuperAdmin || isStoreManager;

  if (!checked || loading) {
    return null;
  }

  if (!isAuthenticated || !userId) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!hasAdminPortalAccess) {
    return <Navigate to="/" replace />;
  }

  if (isStoreManager && !isSuperAdmin && !isLegacyAdmin) {
    const forbiddenPatterns = [
      /^\/admin\/users/i,
      /^\/admin\/contact/i,
      /^\/admin\/about/i,
      /^\/admin\/portfolio/i,
      /^\/admin\/credits/i,
      /^\/admin\/promo-codes/i,
      /^\/admin\/legal/i,
      /^\/admin\/newsletter/i,
      /^\/admin\/services(?!\/orders(?:\/|$))/i,
    ];

    if (forbiddenPatterns.some((pattern) => pattern.test(location.pathname))) {
      return <Navigate to="/admin/access-denied" replace />;
    }
  }

  return children || <Outlet />;
}
